/**
 * DEV-260 — escolhe o widget de cada pergunta. A pergunta (existência, ordem,
 * título, opções, obrigatoriedade) vem da definição; aqui só se decide COMO
 * desenhá-la, pelo `name` estável do template quando o Figma pede um controle
 * específico, e pelo `type` do SurveyJS como reserva.
 */
import { useState } from 'react';
import { cn, Combobox, type ComboboxOption } from '@eureca/ui';
import { ChevronDownIcon } from '@eureca/ui/icons';
import { useDebouncedValue } from '@eureca/ui/forms';
import type { DefinitionQuestion } from '../../shared/definition';
import { MESSAGES } from '../../shared/answer-validation';
import { FIELD_COPY, FIELD_MESSAGES } from '../../shared/field-copy';
import { isAnyDisability, normalizeLabel } from '../../shared/form-rules';
import {
  type CityItem,
  type LabeledItem,
  type NamedItem,
  type StateItem,
  useCatalogue,
} from './catalogue';
import {
  BooleanQuestion,
  FreeTagsQuestion,
  MonthQuestion,
  MultiSelectQuestion,
  SelectQuestion,
  TextareaQuestion,
  TextQuestion,
  type MultiItem,
  type QuestionWidgetProps,
} from './widgets';

export interface QuestionFieldProps {
  question: DefinitionQuestion;
  session: string;
  answers: Record<string, unknown>;
  required: boolean;
  error?: string;
  warning?: string;
  onChange: (value: unknown) => void;
  onBlur: () => void;
}

/** "Qualquer uma" vem do `selectAll` do template (a projeção não o carrega). */
const ANY_DISABILITY = 'Qualquer uma';
const SEARCH_DEBOUNCE_MS = 300;

interface CityValue {
  uf: string;
  city: string;
  cityId?: number;
}

function asArray<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

function stringItems(values: string[]): MultiItem[] {
  return values.map((v) => ({ key: normalizeLabel(v), label: v, stored: v }));
}

export function QuestionField(props: QuestionFieldProps) {
  const { question: q } = props;
  const copy = FIELD_COPY[q.name] ?? {};
  const base: QuestionWidgetProps = {
    name: q.name,
    label: q.title,
    required: props.required,
    hint: copy.hint,
    placeholder: copy.placeholder,
    error: props.error,
    warning: props.warning,
    value: props.answers[q.name],
    onChange: props.onChange,
    onBlur: props.onBlur,
  };

  switch (q.name) {
    case 'numero_posicoes':
    case 'meta_inscritos':
    case 'idade_minima':
    case 'idade_maxima':
      return <TextQuestion {...base} kind="integer" />;
    case 'valor_minimo':
    case 'valor_maximo':
    case 'valor_exato':
      return <TextQuestion {...base} kind="money" />;
    case 'data_minima_formacao':
    case 'data_maxima_formacao':
      return <MonthQuestion {...base} />;
    case 'aceita_todos_cursos':
      return <BooleanQuestion {...base} />;
    case 'local_trabalho':
      return <WorkLocationQuestion {...base} session={props.session} />;
    case 'requisitos_localidade_estado':
      return <StatesQuestion {...base} session={props.session} />;
    case 'requisitos_localidade_cidade':
      return (
        <CitySearchQuestion
          {...base}
          session={props.session}
          hasState={asArray<string>(props.answers.requisitos_localidade_estado).length > 0}
        />
      );
    case 'beneficios':
    case 'pagamentos_adicionais':
      return (
        <CatalogueStringsQuestion
          {...base}
          session={props.session}
          catalogue={q.name === 'beneficios' ? 'benefits' : 'additional-payments'}
        />
      );
    case 'areas_curso':
      return <CourseAreasQuestion {...base} session={props.session} />;
    case 'cursos':
      return (
        <CourseGroupsQuestion
          {...base}
          session={props.session}
          types={asArray<string>(props.answers.tipos_formacao)}
        />
      );
    case 'tipo_deficiencia':
      return <DisabilityTypesQuestion {...base} session={props.session} />;
    case 'competencias':
    case 'diferenciais': {
      // AC-52: item que já está em Competências não entra em Diferenciais.
      const skills = asArray<string>(props.answers.competencias).map(normalizeLabel);
      return (
        <FreeTagsQuestion
          {...base}
          maxItemLength={40}
          emptyMessage={FIELD_MESSAGES.competenciaVazia}
          tooLongMessage={FIELD_MESSAGES.competenciaLonga}
          validateItem={
            q.name === 'diferenciais'
              ? (item) =>
                  skills.includes(normalizeLabel(item)) ? MESSAGES.itemEmCompetencias : null
              : undefined
          }
          itemErrorResetKey={q.name === 'diferenciais' ? skills.join('|') : undefined}
        />
      );
    }
    default:
      break;
  }

  if (q.choices && (q.type === 'checkbox' || q.type === 'tagbox')) {
    const selected = stringItems(asArray<string>(base.value));
    return (
      <MultiSelectQuestion
        {...base}
        options={stringItems(q.choices)}
        selected={selected}
        onItemsChange={(items) => base.onChange(items.map((i) => i.stored))}
      />
    );
  }
  if (q.choices) return <SelectQuestion {...base} choices={q.choices} />;
  if (q.type === 'comment') return <TextareaQuestion {...base} />;
  return <TextQuestion {...base} kind="text" />;
}

type WithSession = QuestionWidgetProps & { session: string };

function CatalogueStringsQuestion(
  p: WithSession & { catalogue: 'benefits' | 'additional-payments' }
) {
  const list = useCatalogue<NamedItem>(p.session, p.catalogue);
  return (
    <MultiSelectQuestion
      {...p}
      options={stringItems(list.items.map((i) => i.name))}
      selected={stringItems(asArray<string>(p.value))}
      onItemsChange={(items) => p.onChange(items.map((i) => i.stored))}
      loading={list.loading}
      failed={list.failed}
      custom={{
        maxLength: 60,
        emptyMessage: FIELD_MESSAGES.beneficioVazio,
        tooLongMessage: FIELD_MESSAGES.beneficioLongo,
        placeholder: FIELD_MESSAGES.novoBeneficioPlaceholder,
      }}
    />
  );
}

/** Estados em ordem alfabética pt-BR (QA 08/10/2026, IDV-004): o catálogo vem por id. */
function sortedStates(items: StateItem[]): StateItem[] {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}

function StatesQuestion(p: WithSession) {
  const list = useCatalogue<StateItem>(p.session, 'states');
  const byUf = new Map(list.items.map((s) => [s.acronym, s.name]));
  const toItem = (uf: string): MultiItem => ({ key: uf, label: byUf.get(uf) ?? uf, stored: uf });
  return (
    <MultiSelectQuestion
      {...p}
      options={sortedStates(list.items).map((s) => toItem(s.acronym))}
      selected={asArray<string>(p.value).map(toItem)}
      onItemsChange={(items) => p.onChange(items.map((i) => i.stored))}
      loading={list.loading}
      failed={list.failed}
    />
  );
}

function cityItem(c: CityValue): MultiItem {
  return { key: `${c.uf}:${normalizeLabel(c.city)}`, label: `${c.city} - ${c.uf}`, stored: c };
}

/**
 * Requisitos de localidade — Cidade. Sem estado escolhido, tentar abrir o
 * campo mostra "Escolha o estado antes da cidade." (DEV-260 AC-28, catálogo
 * 23074) e a lista não abre; com estado, a busca cobre o Brasil todo (anotação
 * do select, 22593). O gatilho bloqueado continua focável: quem usa só teclado
 * também recebe a mensagem.
 */
function CitySearchQuestion(p: WithSession & { hasState: boolean }) {
  const [search, setSearch] = useState('');
  const [askedWithoutState, setAskedWithoutState] = useState(false);
  const debounced = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const q = debounced.trim().length >= 2 ? debounced.trim() : undefined;
  const list = useCatalogue<CityItem>(p.session, 'cities', { q }, q !== undefined && p.hasState);
  const blockedError =
    !p.hasState && askedWithoutState ? FIELD_MESSAGES.estadoAntesDaCidade : undefined;
  return (
    <MultiSelectQuestion
      {...p}
      error={blockedError ?? p.error}
      renderControl={
        p.hasState
          ? undefined
          : (control) => (
              <button
                {...control}
                type="button"
                role="combobox"
                aria-expanded={false}
                data-testid={`question-${p.name}`}
                onClick={() => setAskedWithoutState(true)}
                className={cn(
                  'flex min-h-9 w-full cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 py-2 shadow-2xs transition-colors',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-primary',
                  blockedError && 'border-destructive focus-visible:outline-destructive'
                )}
              >
                <span className="flex-1 truncate text-left text-sm leading-5 text-muted-foreground">
                  {p.placeholder}
                </span>
                <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
              </button>
            )
      }
      options={list.items.map((c) =>
        cityItem({ uf: c.stateAcronym ?? '', city: c.name, cityId: c.id })
      )}
      selected={asArray<CityValue>(p.value).map(cityItem)}
      onItemsChange={(items) => p.onChange(items.map((i) => i.stored))}
      loading={list.loading}
      failed={list.failed}
      onSearchChange={setSearch}
    />
  );
}

function CourseAreasQuestion(p: WithSession) {
  const list = useCatalogue<NamedItem>(p.session, 'course-areas');
  const toItem = (a: NamedItem): MultiItem => ({ key: String(a.id), label: a.name, stored: a });
  return (
    <MultiSelectQuestion
      {...p}
      options={list.items.map(toItem)}
      selected={asArray<NamedItem>(p.value).map(toItem)}
      onItemsChange={(items) => p.onChange(items.map((i) => i.stored))}
      loading={list.loading}
      failed={list.failed}
    />
  );
}

function CourseGroupsQuestion(p: WithSession & { types: string[] }) {
  const types = p.types.join(',');
  const list = useCatalogue<LabeledItem>(p.session, 'course-groups', { types }, types !== '');
  const toItem = (g: LabeledItem): MultiItem => ({ key: String(g.id), label: g.label, stored: g });
  return (
    <MultiSelectQuestion
      {...p}
      options={list.items.map(toItem)}
      selected={asArray<LabeledItem>(p.value).map(toItem)}
      onItemsChange={(items) => p.onChange(items.map((i) => i.stored))}
      loading={list.loading}
      failed={list.failed}
    />
  );
}

function DisabilityTypesQuestion(p: WithSession) {
  const list = useCatalogue<NamedItem>(p.session, 'disability-types');
  const options = stringItems([...list.items.map((i) => i.name), ANY_DISABILITY]);
  const anyKey = normalizeLabel(ANY_DISABILITY);
  return (
    <MultiSelectQuestion
      {...p}
      options={options}
      selected={stringItems(asArray<string>(p.value))}
      onItemsChange={(items) =>
        p.onChange(
          items.some((i) => isAnyDisability(i.label))
            ? [ANY_DISABILITY]
            : items.map((i) => i.stored)
        )
      }
      exclusiveKey={anyKey}
      loading={list.loading}
      failed={list.failed}
    />
  );
}

/** Local de trabalho (22593): UF + cidade lado a lado; cada cidade vira tag. */
function WorkLocationQuestion(p: WithSession) {
  const [uf, setUf] = useState('');
  const [repeatError, setRepeatError] = useState<string | null>(null);
  const states = useCatalogue<StateItem>(p.session, 'states');
  const cities = useCatalogue<CityItem>(p.session, 'cities', { uf }, uf !== '');
  const selected = asArray<CityValue>(p.value);
  const items = selected.map(cityItem);

  const ufOptions: ComboboxOption[] = [...states.items]
    .sort((a, b) => a.acronym.localeCompare(b.acronym, 'pt-BR'))
    .map((s) => ({ value: s.acronym, label: s.acronym }));
  const cityOptions: ComboboxOption[] = cities.items.map((c) => ({
    value: String(c.id),
    label: c.name,
  }));

  function addCity(id: string) {
    const city = cities.items.find((c) => String(c.id) === id);
    if (!city) return;
    const next = cityItem({ uf, city: city.name, cityId: city.id });
    if (items.some((i) => i.key === next.key)) {
      setRepeatError(FIELD_MESSAGES.cidadeRepetida);
      return;
    }
    setRepeatError(null);
    p.onChange([...selected, next.stored]);
    p.onBlur();
  }

  return (
    <MultiSelectQuestion
      {...p}
      error={repeatError ?? p.error}
      options={[]}
      selected={items}
      onItemsChange={(next) => {
        p.onChange(next.map((i) => i.stored));
        p.onBlur();
      }}
      renderControl={(control) => (
        <div className="flex gap-2">
          <div className="w-20 shrink-0">
            <Combobox
              options={ufOptions}
              value={uf || undefined}
              onSelect={(v) => setUf(v)}
              placeholder={FIELD_MESSAGES.uf}
              searchPlaceholder={FIELD_MESSAGES.uf}
              loading={states.loading}
              errorMessage={states.failed ? FIELD_MESSAGES.falhaLista : undefined}
              error={!!(repeatError ?? p.error)}
              data-testid={`question-${p.name}-uf`}
            />
          </div>
          <div className="min-w-0 flex-1">
            <Combobox
              id={control.id}
              aria-describedby={control['aria-describedby']}
              aria-invalid={control['aria-invalid']}
              aria-required={control['aria-required']}
              options={cityOptions}
              value={undefined}
              onSelect={addCity}
              placeholder={p.placeholder}
              searchPlaceholder={p.placeholder}
              disabled={uf === ''}
              loading={cities.loading}
              errorMessage={cities.failed ? FIELD_MESSAGES.falhaLista : undefined}
              emptyMessage={FIELD_MESSAGES.semResultado}
              error={!!(repeatError ?? p.error)}
              data-testid={`question-${p.name}`}
            />
          </div>
        </div>
      )}
    />
  );
}
